'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { ConditionalWrapper } from '@packages/shared/src/components/ConditionalWrapper/ConditionalWrapper';
import { DynamicYield } from '@packages/shared/src/ui/DynamicYield/DynamicYield';
import { useTrackMarketingOnLoad } from '@packages/shared/src/hooks/useTrackCampaign/useTrackCampaign';
import { useEffect } from 'react';
import { Icon } from '../../components/Icon/Icon';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';
import type { UspLinkProps } from './types';
import { TooltipWrapper } from './TooltipWrapper.client';

/**
 * UspLink component.
 *
 * @param {object} props - Component props.
 * @param {string} props.dynamicID - The dynamic ID.
 * @param {object} props.uspItem - The USP item data.
 *
 * @returns JSX.Element The rendered UspLink component.
 */
export const UspLink = ({ dynamicID, uspItem }: UspLinkProps) => {
  const {
    translatable_url: url,
    translatable_title: text,
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
  } = uspItem;
  const { trackClick, trackDisplay } = useTrackMarketingOnLoad(uspItem, uspItem.translatable_url);

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  useEffect(() => {
    trackDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const child = () => (
    <ConditionalWrapper
      condition={!!(tooltipContent && !!tooltipActivation)}
      wrapper={TooltipWrapper(tooltipContent, tooltipPositioning)}
    >
      <span className="leading-3">{text}</span>
    </ConditionalWrapper>
  );

  return (
    <div>
      <DynamicYield dataDyId={dynamicID} />
      {url && (
        <Link
          className="text-size-body-sm flex items-center py-0.5 text-center"
          href={url}
          onClick={() => trackClick()}
        >
          {child()}
          <Icon type="ArrowRight" />
        </Link>
      )}
      {!url && child()}
    </div>
  );
};
